import { inject } from '@angular/core';
import { Router, Params, ParamMap, ActivatedRoute } from '@angular/router';
import { deepEqual } from 'fast-equals';
import { map, distinctUntilChanged } from 'rxjs';

export const patchQueryParamsFactory = () => {
  const router = inject(Router);

  return (queryParams: Params) =>
    router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
};

export const qpObs = <F extends object>(parser: (qp: ParamMap) => F) =>
  inject(ActivatedRoute).queryParamMap.pipe(map(parser), distinctUntilChanged(deepEqual));

export const qpState = <F extends object>(parser: (qp: ParamMap) => F, serializer = (parsed: F): Params => parsed) => {
  const route = inject(ActivatedRoute);
  const patchQueryParams = patchQueryParamsFactory();

  let state: {
    paramMap: ParamMap;
    parsed: F;
  };

  return new Proxy<F>({} as F, {
    get(_target, p) {
      if (typeof p !== 'string') {
        throw new Error(`Unsupported property ${p.toString()}`);
      }

      const paramMap = route.snapshot.queryParamMap;
      if (state?.paramMap !== paramMap) {
        state = { paramMap, parsed: parser(paramMap) };
      }

      return state.parsed[p as keyof F];
    },

    set(_target, p, newValue) {
      if (typeof p !== 'string') {
        throw new Error(`Unsupported property ${p.toString()}`);
      }

      const parsed = {
        ...parser(route.snapshot.queryParamMap),
        [p]: newValue,
      };

      void patchQueryParams(serializer(parsed));

      return true;
    },
  });
};
